import { Component, Input, OnInit, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map, tap, withLatestFrom } from 'rxjs/operators';

import { MultiProductTemplateConfigurations } from '../../configs/multi-product-template.configs';
import { MultiProductTemplateModel } from '../../models/multi-product-types-model';
import { StudioTemplatesService } from '../../services/studio-templates.service';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { getCatalogStyles } from '@pages/category/store/category-page.selectors';
import { ProductViewConfig, CategoryConfiguration, ProductViewPlace, HiddenFieldType, GridProductStyle } from '@shared/product/models';

@Component({
    selector: 'studio-multi-product-template',
    templateUrl: './studio-multi-product-template.component.html',
})

/**
 * Componente para mostrar la plantilla multi product de Zara Studio
 */
export class StudioMultiProductTemplateComponent implements OnInit {
    @Input() public categoryId: string;

    @Input() public products: number[] = [];

    @Input() public categoryConfiguration: CategoryConfiguration;

    @Input() public templateName: string;

    @Input() public place: string;

    @Input() public elementIndex: number;

    @Input() public elementDefaultImageTypes: Map<number, string>;

    @Input() public isMediaCarouselActivated: boolean;

    @Input() public productStyle: GridProductStyle;

    public readonly clickAddToCart = output<ProductPublicModels.PublicProduct>();

    public templateConfiguration$: Observable<ProductViewConfig>;

    public evenTemplateConfiguration$: Observable<ProductViewConfig>;

    public firstTemplateConfiguration$: Observable<ProductViewConfig>;

    public templateSettings: MultiProductTemplateModel;

    public hasCmsStyles: boolean;

    public templateCmsStyles: Record<string, string>;

    public templatePlace: ProductViewPlace;

    public ulClasses: string;

    public liClasses: string;

    public evenLiClasses: string;

    public firstLiClasses: string;

    public skeletonNoDetails: string;

    public templateNameFirst: string;

    constructor(
        private readonly store: Store,
        private readonly multiProductTemplateConfigurations: MultiProductTemplateConfigurations,
    ) { }

    public ngOnInit(): void {
        this.getTemplatePrefix();
        this.initTemplateConfiguration();
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public initTemplateConfiguration(): void {
        this.templateSettings = this.multiProductTemplateConfigurations.getConfig()[this.templateNameFirst];

        if (this.templateSettings) {
            this.hasCmsStyles = !!this.templateSettings.cmsStyles;

            this.templateConfiguration$ = this.store.select(getResolution).pipe(
                first(),
                withLatestFrom(
                    this.store.select(getCatalogStyles),
                    this.store.select(getCmsConfigValue(this.templateSettings.cmsStyles)),
                ),
                tap(() => {
                    const categoryConf = this.findCommonHiddenField();

                    if (categoryConf) {
                        this.setSkeletonNoDetails(categoryConf);
                    }
                }),
                map(([resolution, catalogStyles, cmsStyles]: [string, Record<string, string>, Record<string, string>]) => {
                    const templateConfiguration = StudioTemplatesService.mapTemplateConfiguration(
                        resolution,
                        catalogStyles,
                        cmsStyles,
                        this.templateSettings,
                        this.place
                    );

                    this.templateCmsStyles = cmsStyles;
                    templateConfiguration.showMediaCarousel = this.isMediaCarouselActivated;

                    return templateConfiguration;
                })
            );

            if (this.templateSettings.evenTemplateConfiguration) {
                this.evenTemplateConfiguration$ = this.templateConfiguration$.pipe(
                    first(),
                    map((templateConfiguration: ProductViewConfig) => {
                        return {
                            ...templateConfiguration,
                            imageType: this.templateSettings.evenTemplateConfiguration.imageType,
                            media: this.templateSettings.evenTemplateConfiguration.media,
                            showSecondImageIfPossible:
                                this.templateSettings.evenTemplateConfiguration.showSecondImageIfPossible,
                            showMediaCarousel: this.isMediaCarouselActivated,
                        };
                    })
                );
            }

            if (this.templateSettings?.firstTemplateConfiguration) {
                this.firstTemplateConfiguration$ = this.templateConfiguration$.pipe(
                    first(),
                    withLatestFrom(this.store.select(getResolution)),
                    map(([templateConfiguration, _resolution]: [ProductViewConfig, Resolutions]) => {
                        return {
                            ...templateConfiguration,
                            imageType: this.templateSettings.firstTemplateConfiguration.imageType,
                            media: this.templateSettings.firstTemplateConfiguration.media,
                            showSecondImageIfPossible:
                                this.templateSettings.firstTemplateConfiguration.showSecondImageIfPossible,
                            showMediaCarousel: this.isMediaCarouselActivated,
                        };
                    }),
                );
            }
        } else {
            console.error(`Settings for template ${this.templateName} not found on MultiProductTemplate`);
        }
    }

    public getTemplatePrefix(): void {
        if (this.templateName) {
            const parts = this.templateName.split('_');
            this.templateNameFirst = parts.length > 1 ? parts[0] : this.templateName;
        }
    }

    public addToCartSheet({ product }) {
        this.clickAddToCart.emit(product);
    }

    private findCommonHiddenField(): HiddenFieldType | undefined {
        const hiddenFieldsProducts = this.products.map(productId =>
            this.categoryConfiguration.productConf.get(productId)?.hiddenField);
        const firstElement = hiddenFieldsProducts?.pop();

        const isSameFirst = hiddenFieldsProducts.every(element => element === firstElement);
        return isSameFirst ? firstElement : undefined;
    }

    private setSkeletonNoDetails(hiddenField: HiddenFieldType): void {
        const skeletonMap = {
            all: 'sk-nodetails',
            description: 'sk-nodescription',
        };

        this.skeletonNoDetails = skeletonMap[hiddenField] || '';
    }
}
